/**
 * Theme store (06 §3). Persists `sinyal.theme` = light|dark|system to
 * localStorage and reflects the resolved theme onto <html data-theme>.
 */

import { create } from 'zustand';

export type ThemePref = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'sinyal.theme';

function readPref(): ThemePref {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function systemDark(): boolean {
  return (
    typeof matchMedia !== 'undefined' &&
    matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function resolve(pref: ThemePref): ResolvedTheme {
  if (pref === 'system') return systemDark() ? 'dark' : 'light';
  return pref;
}

function apply(resolved: ResolvedTheme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved);
  }
}

interface ThemeState {
  pref: ThemePref;
  resolved: ResolvedTheme;
  setPref: (p: ThemePref) => void;
  /** Re-evaluate (call when system scheme changes). */
  syncSystem: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  pref: readPref(),
  resolved: resolve(readPref()),
  setPref: (pref) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, pref);
    const resolved = resolve(pref);
    apply(resolved);
    set({ pref, resolved });
  },
  syncSystem: () => {
    const { pref } = get();
    if (pref !== 'system') return;
    const resolved = resolve(pref);
    apply(resolved);
    set({ resolved });
  },
}));

/** Wire up system-scheme change listener once at boot. */
export function initThemeSync(): void {
  if (typeof matchMedia === 'undefined') return;
  const mq = matchMedia('(prefers-color-scheme: dark)');
  const handler = () => useThemeStore.getState().syncSystem();
  mq.addEventListener?.('change', handler);
}
